import { useQuery } from "@tanstack/react-query";
import { Space, Tag } from "antd";
import type { TableColumnsType } from "antd";
import { Link } from "react-router-dom";
import { fetchTasks } from "../../../lib/api/resources/tasks";
import { buildTaskColumns } from "./lib/constants/taskColumns";
import { useCompleteTaskMutation } from "./lib/hooks/useCompleteTaskMutation";
import { CheckOutlined, PlusOutlined, RobotOutlined } from "@ant-design/icons";
import { useMemo, useState } from "react";
import { TaskFilterValues } from "./lib/types/TaskFilterValues.interface";
import { buildTaskApiFilters } from "./lib/helpers/buildTaskApiFilters";
import { TasksFilterModal } from "./components/TasksFilterModal";
import { TasksColumnsModal } from "./components/TasksColumnsModal";
import { Table } from "@/components/v2";
import { Button } from "@rumbledirect/ui";
import type { Task } from "@rumble-it/common-lib/types/models/Task.interface";
import { autoGeneratedTasks } from "../claims/data/autoGeneratedTasksData";

const AUTO_TASK_PREFIX = "auto-task-";

const isAutoGenerated = (record: Task) =>
  record.id.startsWith(AUTO_TASK_PREFIX);

export const TasksList = () => {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<TaskFilterValues>({ status: "open" });

  const setTaskFilters = (filterValues: TaskFilterValues) => {
    setFilters(filterValues);
    setPage(1);
  };

  const tasksQuery = useQuery({
    queryKey: ["tasks", page, filters],
    queryFn: () =>
      fetchTasks({
        page,
        ...buildTaskApiFilters(filters),
      }),
  });
  const completeMutation = useCompleteTaskMutation();

  const baseColumns = buildTaskColumns(completeMutation);

  const columns: TableColumnsType<Task> = useMemo(() => {
    return baseColumns.map((col) => {
      if (col.key === "description") {
        return {
          ...col,
          render: (text: string, record: Task) => {
            const originalRender = col.render as (
              text: string,
              record: Task,
            ) => React.ReactNode;

            return (
              <>
                {isAutoGenerated(record) ? (
                  <span
                    style={
                      record.isCompleted
                        ? { color: "rgba(0, 0, 0, 0.45)" }
                        : undefined
                    }
                  >
                    {record.description}
                  </span>
                ) : (
                  originalRender(text, record)
                )}
                {isAutoGenerated(record) && record.isCompleted && (
                  <Tag
                    icon={<RobotOutlined />}
                    color="purple"
                    style={{ marginLeft: 8 }}
                  >
                    Automatisch
                  </Tag>
                )}
              </>
            );
          },
        };
      }

      if (col.key === "actions") {
        return {
          ...col,
          render: (_text: string, record: Task) => {
            if (isAutoGenerated(record) && record.isCompleted) return null;
            if (isAutoGenerated(record)) {
              return (
                <Button icon={<CheckOutlined />} onClick={() => {}}>
                  Afvinken
                </Button>
              );
            }
            const originalRender = col.render as (
              text: string,
              record: Task,
            ) => React.ReactNode;
            return originalRender(_text, record);
          },
        };
      }

      return col;
    });
  }, [baseColumns]);

  const [visibleKeys, setVisibleKeys] = useState<string[]>(
    baseColumns.map((col) => col.key as string).filter(Boolean),
  );

  const visibleColumns = useMemo(
    () =>
      columns.filter(
        (col) => col.key && visibleKeys.includes(col.key as string),
      ),
    [columns, visibleKeys],
  );

  const allTasks = useMemo(() => {
    const apiTasks = tasksQuery?.data?.member ?? [];
    return [...autoGeneratedTasks, ...apiTasks];
  }, [tasksQuery?.data?.member]);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Space
          size="middle"
          wrap
          style={{ width: "100%", justifyContent: "space-between" }}
        >
          <Space size="middle" wrap>
            <TasksFilterModal
              setTaskFilters={setTaskFilters}
              taskFilters={filters}
            />
            <TasksColumnsModal
              visibleKeys={visibleKeys}
              setVisibleKeys={setVisibleKeys}
              columns={baseColumns}
            />
          </Space>
          <Link to="/claims/tasks/create">
            <Button icon={<PlusOutlined />}>Nieuwe taak</Button>
          </Link>
        </Space>
      </div>
      <Table
        rowKey="id"
        columns={visibleColumns}
        dataSource={allTasks}
        loading={tasksQuery.isLoading}
        pagination={{
          pageSize: 50,
          total: (tasksQuery.data?.totalItems ?? 0) + autoGeneratedTasks.length,
          showSizeChanger: false,
          onChange: (page) => setPage(page),
        }}
      />
    </div>
  );
};
